import { type CSSProperties } from "react";
import { Command as Cmdk } from "cmdk";
import { KIND_COLOR, KIND_LABEL, type Command, type CommandKind } from "./types";

/**
 * Cheatsheet mode (S3.21) — the full command list grouped by category, rendered
 * from the live {@link useCommandIndex} output (never a hardcoded list, so new
 * commands appear automatically). Interactive: ↵ runs the highlighted command.
 * Each group is capped so a large workspace's navigation set stays a reference
 * rather than an exhaustive dump — "type to search" finds the rest.
 */

const ORDER: CommandKind[] = ["nav", "create", "prompt", "action", "system"];

const GROUP_TITLE: Record<CommandKind, string> = {
  nav: "Navigation",
  create: "Create",
  prompt: "Prompts",
  action: "Actions",
  system: "System",
  recent: "Recent",
};

const PER_GROUP_CAP = 30;

export function CheatSheet({ all, onRun }: { all: Command[]; onRun: (c: Command) => void }) {
  const grouped: Partial<Record<CommandKind, Command[]>> = {};
  for (const c of all) (grouped[c.kind] ??= []).push(c);

  return (
    <>
      <div style={help}>Type to search. ↑↓ to navigate. ↵ to execute. ⎋ to close.</div>
      {ORDER.map((kind) => {
        const items = grouped[kind] ?? [];
        if (items.length === 0) return null;
        const shown = items.slice(0, PER_GROUP_CAP);
        const hidden = items.length - shown.length;
        return (
          <Cmdk.Group key={kind} heading={GROUP_TITLE[kind]} className="kuvio-cmdk-group">
            {shown.map((cmd) => (
              <Cmdk.Item
                key={`cheat:${cmd.id}`}
                value={`cheat:${cmd.id}`}
                onSelect={() => onRun(cmd)}
                className="kuvio-cmdk-item"
              >
                <i
                  className={`ti ti-${cmd.icon ?? "circle"}`}
                  style={{ fontSize: 16, color: KIND_COLOR[cmd.kind], width: 18, textAlign: "center", flexShrink: 0 }}
                  aria-hidden="true"
                />
                <span style={label}>{cmd.label}</span>
                {cmd.subtitle ? <span style={sub}>{cmd.subtitle}</span> : null}
                <span style={kindLabel}>{KIND_LABEL[cmd.kind]}</span>
              </Cmdk.Item>
            ))}
            {hidden > 0 && (
              <div style={moreHint}>+{hidden} more — type to search</div>
            )}
          </Cmdk.Group>
        );
      })}
    </>
  );
}

const help: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-secondary)",
  padding: "10px 12px",
  borderBottom: "var(--kuvio-border-default)",
  marginBottom: 4,
};

const label: CSSProperties = {
  fontSize: 14,
  fontWeight: 500,
  color: "var(--color-text-primary)",
  whiteSpace: "nowrap",
  flexShrink: 0,
};

const sub: CSSProperties = {
  fontSize: 12,
  color: "var(--color-text-tertiary)",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
  flex: 1,
  minWidth: 0,
};

const kindLabel: CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  color: "var(--color-text-tertiary)",
  textTransform: "uppercase",
  letterSpacing: "0.04em",
  flexShrink: 0,
  marginLeft: "auto",
};

const moreHint: CSSProperties = {
  fontSize: 11,
  color: "var(--color-text-tertiary)",
  padding: "4px 12px 8px",
};
